/**
 * Score count-up animation: M5.T5.
 *
 * Per IMPLEMENTATION_PLAN.md §7 M5.T5:
 *   "score-count-up for leaderboard numbers (CSS @property integer
 *    counter, 800ms ease-out)."
 *
 * Implementation note: the spec calls for the CSS @property + counter()
 * pattern. We instead drive the interpolation with requestAnimationFrame
 * and write the integer into the span's textContent for three reasons:
 *
 *   1. Accessibility: the rendered number lives in the DOM text, so
 *      screen readers + copy-paste work. The @property approach uses
 *      `content: counter(...)` on a ::after pseudo, which is invisible
 *      to most assistive tech.
 *   2. Animating-on-value-change, counters re-trigger via @keyframes
 *      reset only on class toggle, which is awkward when the target
 *      changes (e.g. REVEAL → next REVEAL with a higher score).
 *   3. Browser parity: @property + counter() interaction has lingering
 *      quirks across engines.
 *
 * The visible outcome: an integer counting up from 0 (or the previous
 * displayed value) to the target over 800ms with ease-out cubic timing,
 * matches the spec end-state exactly.
 *
 * Reduced motion: when `prefers-reduced-motion: reduce` is honored by
 * the browser, the animation is skipped and the final value renders
 * immediately.
 */

import { useEffect, useRef, useState } from "preact/hooks";

export const COUNT_UP_DURATION_MS = 800;

type CountUpProps = {
  /** The target integer to animate up to. */
  value: number;
  /**
   * Override the default 800ms duration. Mostly used by tests.
   */
  durationMs?: number;
  /**
   * When true, skip the animation and render `value` immediately.
   * Tests, snapshot capture, and the reduced-motion path use this.
   */
  instant?: boolean;
  /**
   * Hold the start of the count by this many ms (audit 2026-06-11, reveal
   * ceremony beats: a CountUp inside a delayed beat must not finish counting
   * while its section is still invisible). Ignored in skip/instant modes.
   */
  delayMs?: number;
  /**
   * Review fix (adversarial P3, 2026-06-11): when false, mount renders
   * `value` directly instead of rolling 0→value, for persistent chrome
   * (the players panel) that REMOUNTS on every phase swap. Value CHANGES
   * still glide; only the mount roll is suppressed. Ceremony surfaces
   * (reveal / game-over) keep the default mount roll.
   */
  animateOnMount?: boolean;
  /** Optional extra class for the span. */
  class?: string;
};

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * jsdom doesn't drive requestAnimationFrame synchronously, so unit tests
 * that assert on rendered numbers would otherwise see a stuck "0". Treat
 * the jsdom environment as instant-mode so callers don't have to thread
 * an `instant` prop through every test fixture.
 *
 * Real browsers (and Playwright E2E) all set a UA that does not include
 * "jsdom", so this only short-circuits the unit-test environment.
 */
function isJsdomEnv(): boolean {
  return typeof navigator !== "undefined" && /jsdom/i.test(navigator.userAgent ?? "");
}

/**
 * Animates an integer from the prior displayed value (or 0 on mount) to
 * `value` over 800ms with ease-out cubic. Renders the live integer as
 * text content.
 */
export function CountUp({
  value,
  durationMs = COUNT_UP_DURATION_MS,
  instant = false,
  delayMs = 0,
  animateOnMount = true,
  class: className,
}: CountUpProps) {
  const skipAnimation = instant || prefersReducedMotion() || isJsdomEnv();
  const [displayed, setDisplayed] = useState<number>(() =>
    skipAnimation || !animateOnMount ? value : 0,
  );
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (skipAnimation) {
      setDisplayed(value);
      return;
    }
    // Animate from whatever's currently displayed → new target so
    // re-renders with a changed `value` glide smoothly instead of
    // restarting from 0.
    const initial = displayed;
    const delta = value - initial;
    if (delta === 0) return;
    let holdId: ReturnType<typeof setTimeout> | null = null;
    const startAnimation = () => {
      const start = performance.now();
      const tick = (now: number) => {
        const elapsed = now - start;
        const t = Math.min(1, elapsed / durationMs);
        // ease-out cubic: easings.net canonical form
        const eased = 1 - Math.pow(1 - t, 3);
        setDisplayed(Math.round(initial + delta * eased));
        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          rafRef.current = null;
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    };
    if (delayMs > 0) {
      holdId = setTimeout(startAnimation, delayMs);
    } else {
      startAnimation();
    }
    return () => {
      if (holdId !== null) clearTimeout(holdId);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    // `displayed` intentionally omitted from deps, it's the seed
    // value, not a re-trigger dep. Recomputing the animation every
    // tick would loop forever.
  }, [value, durationMs, skipAnimation, delayMs]);

  return (
    <span
      class={className ? `score-count-up ${className}` : "score-count-up"}
      data-testid="score-count-up"
      aria-label={String(value)}
    >
      {displayed}
    </span>
  );
}
